import React from 'react';
import { PropTypes } from 'prop-types';
import { useSelector } from 'react-redux';

import { useGetAggregatedPromoterDataQuery, useGetAggregatedSGPDataQuery } from 'api/main';
import { userSelectors } from 'reducers';
import { texts } from 'utils/languages/fr';

import DashboardSection from '../common-components/DashboardSection';
import InfoCard from '../common-components/InfoCard';
import { Loader } from 'components';
import { Typography, Box, Grid } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';
import { bankingStatementStatus, roleCategories } from 'utils/constants';
import { useUserRoleMatchCategory } from 'utils/hooks';

const useStyles = makeStyles(theme => ({
    container: {
        width: '80%',
        paddingLeft: theme.spacing(2),
        [theme.breakpoints.down('md')]: {
            width: '100%',
        },
    },
    infoContainer: {
        paddingBottom: theme.spacing(3),
        marginBottom: theme.spacing(2),
        borderBottom: `1px solid ${theme.palette.grey[400]}`,
    },
    gridRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        [theme.breakpoints.down('md')]: {
            padding: 0,
        },
        cursor: 'pointer',
        padding: '0 15% 0 8%',
        '&:hover': {
            color: theme.palette.primary.main,
        },
        '&:first-child': {
            cursor: 'initial',
            padding: '0 5% 0 10%',
            '&:hover': {
                color: 'initial',
            },
        },
    },
    subtitle: {
        paddingLeft: theme.spacing(1),
        fontWeight: 600,
    },
    subsection: {
        paddingLeft: theme.spacing(3),
        fontWeight: 600,
    },
}));

const ActionsToTake = ({ onListFilter }) => {
    const classes = useStyles();
    const internalUser = useSelector(userSelectors.getInternalUser);
    const userIsPromoter = useUserRoleMatchCategory(roleCategories.PROMOTERS);
    const isServicer = useUserRoleMatchCategory(roleCategories.SERVICERS);

    const { data: sgpAggregatedData = null, isFetching: sgpDataLoader } =
        useGetAggregatedSGPDataQuery({}, { skip: userIsPromoter });
    const { data: promoterAggregatedData = null, isFetching: promoterDataLoader } =
        useGetAggregatedPromoterDataQuery({}, { skip: !userIsPromoter });
    const aggregatedData = sgpAggregatedData || promoterAggregatedData || {};

    return (
        <DashboardSection title={texts.ACTIONS_TO_TAKE}>
            <Loader loading={sgpDataLoader || promoterDataLoader} marginTop={10}>
                <Typography variant="body1" gutterBottom className={classes.subtitle}>
                    {texts.PARTNERS}
                </Typography>
                <Box width="100%" marginBottom={3}>
                    <Typography variant="body1" gutterBottom className={classes.subsection}>
                        {texts.INTERRUPTED_INSCRIPTIONS}
                    </Typography>
                    <Grid container spacing={0} direction="column" className={classes.container}>
                        <Grid item className={classes.gridRow}>
                            <Typography>{texts.PERIOD}</Typography>
                            <Typography>{texts.INSCRIPTIONS_NUMBER}</Typography>
                        </Grid>
                        <Grid
                            item
                            className={classes.gridRow}
                            onClick={onListFilter({ distributor_activity_0_3: 1 }, 'distributors')}
                        >
                            <Typography>0 - 3 {texts.DAYS}</Typography>
                            <Typography>
                                {aggregatedData.distributor_less_than_3_days ?? 0}
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            className={classes.gridRow}
                            onClick={onListFilter({ distributor_activity_3_7: 1 }, 'distributors')}
                        >
                            <Typography>4 - 7 {texts.DAYS}</Typography>
                            <Typography>
                                {aggregatedData.distributor_between_4_and_7_days ?? 0}
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            className={classes.gridRow}
                            onClick={onListFilter({ distributor_activity_7_30: 1 }, 'distributors')}
                        >
                            <Typography>8 - 30 {texts.DAYS}</Typography>
                            <Typography>
                                {aggregatedData.distributor_between_8_and_30_days ?? 0}
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            className={classes.gridRow}
                            onClick={onListFilter(
                                { distributor_activity_30_plus: 1 },
                                'distributors'
                            )}
                        >
                            <Typography>30 {texts.DAYS} +</Typography>
                            <Typography>
                                {aggregatedData.distributor_more_than_30_days ?? 0}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                <Grid container spacing={2}>
                    {!isServicer && (
                        <InfoCard
                            onClick={onListFilter({ conventionPartiallySigned: 1 }, 'distributors')}
                            number={aggregatedData.nr_conventions_partially_signed}
                            label={texts.CONVENTIONS_TO_BE_SIGNED}
                        />
                    )}
                    <InfoCard
                        onClick={onListFilter({ pendingCIFValidation: 1 }, 'subscriptions')}
                        number={aggregatedData.nr_subscriptions_pending_cif_validation}
                        label={texts.SUBSCRIPTIONS_TO_BE_VERIFIED}
                    />
                </Grid>

                <Box className={classes.infoContainer}></Box>
                <Typography variant="body1" gutterBottom className={classes.subtitle}>
                    {texts.SUBSCRIPTIONS}
                </Typography>
                <Box width="100%" marginBottom={3}>
                    <Typography variant="body1" gutterBottom className={classes.subsection}>
                        {texts.INTERRUPTED_SUBSCRIPTIONS}
                    </Typography>
                    <Grid container spacing={0} direction="column" className={classes.container}>
                        <Grid item className={classes.gridRow}>
                            <Typography>{texts.PERIOD}</Typography>
                            <Typography>{texts.INSCRIPTIONS_NUMBER}</Typography>
                        </Grid>
                        <Grid
                            item
                            className={classes.gridRow}
                            onClick={onListFilter({ activity_0_3: 1 }, 'subscriptions')}
                        >
                            <Typography>0 - 3 {texts.DAYS}</Typography>
                            <Typography>{aggregatedData.less_than_3_days ?? 0}</Typography>
                        </Grid>
                        <Grid
                            item
                            className={classes.gridRow}
                            onClick={onListFilter({ activity_3_7: 1 }, 'subscriptions')}
                        >
                            <Typography>4 - 7 {texts.DAYS}</Typography>
                            <Typography>{aggregatedData.between_4_and_7_days ?? 0}</Typography>
                        </Grid>
                        <Grid
                            item
                            className={classes.gridRow}
                            onClick={onListFilter({ activity_7_30: 1 }, 'subscriptions')}
                        >
                            <Typography>8 - 30 {texts.DAYS}</Typography>
                            <Typography>{aggregatedData.between_8_and_30_days ?? 0}</Typography>
                        </Grid>
                        <Grid
                            item
                            className={classes.gridRow}
                            onClick={onListFilter({ activity_30_plus: 1 }, 'subscriptions')}
                        >
                            <Typography>30 {texts.DAYS} +</Typography>
                            <Typography>{aggregatedData.more_than_30_days ?? 0}</Typography>
                        </Grid>
                    </Grid>
                </Box>
                <Grid container spacing={2}>
                    <InfoCard
                        onClick={onListFilter({ pendingServicerValidation: 1 }, 'subscriptions')}
                        number={aggregatedData.nr_subscriptions_pending_servicer_validation}
                        label={texts.SUBSCRIPTIONS_TO_BE_VALIDATED}
                    />
                    {!isServicer && (
                        <InfoCard
                            onClick={onListFilter(
                                { pendingSGPSignature: 1, fundSGPId: internalUser?.sgp?.id },
                                'subscriptions'
                            )}
                            number={aggregatedData.nr_subscriptions_pending_sgp_signature}
                            label={texts.SUBSCRIPTIONS_TO_BE_SIGNED}
                        />
                    )}
                    <InfoCard
                        onClick={onListFilter({ alertTypes: ['critical'] }, 'subscriptions')}
                        number={aggregatedData.nr_critical_warnings}
                        label={texts.CRITICAL_WARNING_ONGOING_SUBSCRIPTIONS}
                    />

                    <InfoCard
                        onClick={onListFilter(
                            { bankingStatementStatus: bankingStatementStatus.PENDING_VALIDATION },
                            'investors'
                        )}
                        number={
                            aggregatedData.nr_subscriptions_pending_banking_statement_servicer_validation
                        }
                        label={texts.RIB_CHANGES_TO_VALIDATE}
                    />
                </Grid>
            </Loader>
        </DashboardSection>
    );
};
ActionsToTake.propTypes = {
    onListFilter: PropTypes.func,
};

export default ActionsToTake;
